import { type ReactNode, useMemo, useState } from 'react';

import type { RouteRef, SubRouteRef } from '@backstage/core-plugin-api';

import { Page } from '@spotify/confidence-common-react';
import {
  FilterBar,
  FiltersProvider,
  type ViewMode,
  ViewModeToggle,
} from '@spotify/confidence-core-react';
import { FeatureFlagged } from '@spotify/confidence-flag-evaluation-react';
import { FavoritesFilterToggle } from '@spotify/plugin-confidence-collections-react';

import { type WorkflowExtensionOptions } from '../../extensions';
import { useWorkflowExtensions } from '../../hooks';
import { CreateExperimentMenu } from './CreateExperimentMenu';

type FiltersProviderProps = Parameters<typeof FiltersProvider>[0];

export type ExperimentsPageLayoutProps = {
  routeRef: RouteRef | SubRouteRef;
  storageKey: string;
  filters: FiltersProviderProps['filters'];
  filterExtensions?: (
    extensions: WorkflowExtensionOptions[],
  ) => WorkflowExtensionOptions[];
  renderTable: (props: { mode: ViewMode }) => ReactNode;
};

export const ExperimentsPageLayout = ({
  routeRef,
  storageKey,
  filters,
  filterExtensions,
  renderTable,
}: ExperimentsPageLayoutProps) => {
  const [mode, setMode] = useState<ViewMode>('Table');
  const extensions = useWorkflowExtensions();

  const visibleExtensions = useMemo(
    () => (filterExtensions ? filterExtensions(extensions) : extensions),
    [extensions, filterExtensions],
  );

  const creatableExtensions = useMemo(
    () => visibleExtensions.filter(ext => ext.createComponent),
    [visibleExtensions],
  );

  return (
    <Page.Root routeRef={routeRef}>
      <Page.Wrapper
        component={FiltersProvider}
        storageKey={storageKey}
        searchField="displayName"
        filters={filters}
      />
      <Page.Content scrollButton>
        <Page.ContentActionBar>
          <ViewModeToggle mode={mode} onModeChange={setMode} />
          <FilterBar>
            <FeatureFlagged with="collections">
              <FavoritesFilterToggle />
            </FeatureFlagged>
          </FilterBar>
          {creatableExtensions.length > 0 && (
            <CreateExperimentMenu extensions={creatableExtensions} />
          )}
        </Page.ContentActionBar>
        <Page.ContentMain>{renderTable({ mode })}</Page.ContentMain>
      </Page.Content>
    </Page.Root>
  );
};
